package com.example.hukakmp.util

import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

private fun dosDigitos(n: Int): String = n.toString().padStart(2, '0')

fun formatearFechaDMY(fecha: LocalDate): String =
    "${dosDigitos(fecha.dayOfMonth)}/${dosDigitos(fecha.monthNumber)}/${fecha.year}"

/** Acepta yyyy-MM-dd o dd/MM/yyyy y valida también fechas imposibles (31/02, etc.). */
fun parsearFecha(fecha: String): LocalDate? {
    val texto = fecha.trim()
    if (texto.isEmpty()) return null

    return try {
        when {
            '-' in texto -> {
                val partes = texto.split('-')
                if (partes.size != 3) return null
                LocalDate(
                    year = partes[0].toIntOrNull() ?: return null,
                    monthNumber = partes[1].toIntOrNull() ?: return null,
                    dayOfMonth = partes[2].toIntOrNull() ?: return null,
                )
            }

            '/' in texto -> {
                val partes = texto.split('/')
                if (partes.size != 3) return null
                LocalDate(
                    year = partes[2].toIntOrNull() ?: return null,
                    monthNumber = partes[1].toIntOrNull() ?: return null,
                    dayOfMonth = partes[0].toIntOrNull() ?: return null,
                )
            }

            else -> null
        }
    } catch (_: IllegalArgumentException) {
        null
    }
}

/** Devuelve siempre dd/MM/yyyy. */
fun normalizarFecha(fecha: String): String? = parsearFecha(fecha)?.let(::formatearFechaDMY)

fun fechaHoyLocal(): LocalDate =
    kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

fun fechaHoyDMY(): String = formatearFechaDMY(fechaHoyLocal())

fun esFechaFutura(fecha: String): Boolean =
    parsearFecha(fecha)?.let { it > fechaHoyLocal() } ?: false

/** Convierte HH:mm a minutos, rechazando horas fuera de 00:00..23:59. */
fun horaAMinutos(hora: String): Int? {
    val partes = hora.trim().split(':')
    if (partes.size != 2) return null
    val horas = partes[0].toIntOrNull() ?: return null
    val minutos = partes[1].toIntOrNull() ?: return null
    if (horas !in 0..23 || minutos !in 0..59) return null
    return horas * 60 + minutos
}

/** Para una jornada de hoy, evita seleccionar horarios que todavía no ocurrieron. */
fun esHoraFuturaHoy(fecha: String, hora: String): Boolean {
    val fechaSeleccionada = parsearFecha(fecha) ?: return false
    val hoy = fechaHoyLocal()
    if (fechaSeleccionada != hoy) return false

    val minutosSeleccionados = horaAMinutos(hora) ?: return false
    val ahora = kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).time
    val minutosActuales = ahora.hour * 60 + ahora.minute
    return minutosSeleccionados > minutosActuales
}

/** "Xh Ymin" entre HH:mm. Si fin es menor que inicio se interpreta como jornada nocturna. */
fun calcularDuracion(horaInicio: String?, horaFin: String?): String? {
    if (horaInicio.isNullOrBlank() || horaFin.isNullOrBlank()) return null
    val inicio = horaAMinutos(horaInicio) ?: return null
    val fin = horaAMinutos(horaFin) ?: return null
    var diferencia = fin - inicio
    if (diferencia < 0) diferencia += 24 * 60
    val horas = diferencia / 60
    val minutos = diferencia % 60
    return when {
        horas == 0 -> "${minutos}min"
        minutos == 0 -> "${horas}h"
        else -> "${horas}h ${minutos}min"
    }
}

fun generarIdParte(): String {
    val millis = kotlin.time.Clock.System.now().toEpochMilliseconds()
    val aleatorio = (0..999_999).random()
    return "parte_${millis}_$aleatorio"
}
